import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Success360Service from "../../Services/Success360Service";
import Spinner from "react-bootstrap/esm/Spinner";

export default function EditSuccess360({ show, onHide, onSuccess, editData }) {
  const initialFormData = {
    title: "",
    description: "",
    durationType: "",
    examType: "",
    costPrice: "",
    sellingPrice: "",
    discount: "",
    expiryDate: "",
    isPopular: false,
    statusId: 1,
  };

  const [formData, setFormData] = useState(initialFormData);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const examTypes = ["TOEFL", "IELTS", "PTE", "GRE", "GMAT", "SAT"];

  useEffect(() => {
    if (editData && show) {
      setFormData({
        title: editData.title || "",
        description: Array.isArray(editData.description)
          ? editData.description.join("\n")
          : editData.description || "",
        durationType: editData.durationType || "",
        examType: editData.examType || "",
        costPrice: editData.costPrice || "",
        sellingPrice: editData.sellingPrice || "",
        discount: editData.discount || "",
        expiryDate: editData.expiryDate
          ? editData.expiryDate.split(" ")[0]
          : "",
        isPopular: editData.isPopular || false,
        statusId: editData.statusId || 1,
      });
    }
  }, [editData, show]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    setValidated(true);

    if (form.checkValidity() === false) {
      event.stopPropagation();
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        costPrice: parseFloat(formData.costPrice) || 0,
        sellingPrice: parseFloat(formData.sellingPrice) || 0,
        discount: parseFloat(formData.discount) || 0,
        statusId: parseInt(formData.statusId),
        description: formData.description
          ?.split("\n")
          .map((item) => item.trim())
          .filter(Boolean),
      };

      const response = await Success360Service.updateSuccess360(
        editData.id,
        submitData
      );

      if (response.data.success) {
        toast.success(
          response.data.message || "Success360 plan updated successfully!"
        );

        setFormData(initialFormData);
        setValidated(false);
        onSuccess?.();
        onHide();
      } else {
        toast.error(
          response.data.message || "Failed to update Success360 plan"
        );
      }
    } catch (error) {
      console.error("Error updating Success360 plan:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to update Success360 plan. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setValidated(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-pencil-square me-2"></i>
          Edit Success360 Plan
        </Modal.Title>
      </Modal.Header>

      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Body>
          <Row className="g-3">
            <Col md={12}>
              <FloatingLabel controlId="title" label="Plan Title *">
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter plan title"
                  required
                  maxLength={100}
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid title.
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col md={12}>
              <FloatingLabel
                controlId="description"
                label="Description * (Each line will be a separate item)"
              >
                <Form.Control
                  as="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter plan description (Each line will be a separate item)"
                  required
                  style={{ height: "100px", width: "450px", fontSize: "14px" }}
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a description.
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col md={6}>
              <FloatingLabel controlId="examType" label="Exam Type *">
                <Form.Select
                  name="examType"
                  value={formData.examType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Exam Type</option>
                  {examTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Please select an exam type.
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            {/* Duration Type */}
            <Col md={6}>
              <FloatingLabel
                controlId="durationType"
                label="Duration Type * (Months/Years)"
              >
                <Form.Control
                  type="text"
                  name="durationType"
                  value={formData.durationType}
                  onChange={handleInputChange}
                  placeholder="Duration Type (Months/Years)"
                  required
                  maxLength={50}
                />
                <Form.Control.Feedback type="invalid">
                  Please provide a valid duration type.
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Row className="g-3">
              <Col md={4}>
                <FloatingLabel controlId="costPrice" label="Cost Price *">
                  <Form.Control
                    type="number"
                    name="costPrice"
                    value={formData.costPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid cost price.
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel controlId="sellingPrice" label="Selling Price *">
                  <Form.Control
                    type="number"
                    name="sellingPrice"
                    value={formData.sellingPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    required
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid selling price.
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col md={4}>
                <FloatingLabel controlId="discount" label="Discount *">
                  <Form.Control
                    type="number"
                    name="discount"
                    value={formData.discount}
                    onChange={handleInputChange}
                    placeholder="0"
                    required
                    min="0"
                    step="0.01"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid discount amount.
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>

            <Col md={6}>
              <FloatingLabel controlId="expiryDate" label="Expiry Date *">
                <Form.Control
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split("T")[0]}
                />
                <Form.Control.Feedback type="invalid">
                  Please select a valid expiry date.
                </Form.Control.Feedback>
              </FloatingLabel>
            </Col>

            <Col md={6} className="d-flex align-items-center">
              <Form.Check
                type="checkbox"
                id="isPopular"
                name="isPopular"
                label="Mark as Popular Plan"
                checked={formData.isPopular}
                onChange={handleInputChange}
                className="mt-3"
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            className="d-flex align-items-center"
          >
            {submitting ? (
              <>
                <Spinner animation="border" variant="light" />
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Update Plan
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
